<template>
  <div class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
    <div class="bg-white w-full max-w-md rounded-lg shadow-2xl transform transition-all">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 class="text-lg font-semibold text-gray-900">Add Tag</h2>
        <button
          @click="closeForm"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form @submit.prevent="submitForm" class="p-6 space-y-5">
        <!-- Tag Type Selection -->
        <div>
          <label for="tagType" class="block text-sm font-medium text-gray-700 mb-1.5">
            Tag Type
          </label>
          <select
            id="tagType"
            v-model="selectedTagTypeId"
            @change="onTagTypeChange"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all"
            required
          >
            <option value="" disabled>Select a type...</option>
            <option
              v-for="tagType in sortedTagTypes"
              :key="tagType.id"
              :value="tagType.id"
            >
              {{ tagType.name }}
            </option>
          </select>
        </div>

        <!-- Tag Selection -->
        <div v-if="selectedTagTypeId" class="animate-fadeIn">
          <label for="tag" class="block text-sm font-medium text-gray-700 mb-1.5">
            Tag
          </label>
          <select
            id="tag"
            v-model="selectedTagId"
            @change="onTagChange"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all"
            required
          >
            <option value="" disabled>Select a tag...</option>
            <option
              v-for="tag in sortedTags"
              :key="tag.id"
              :value="tag.id"
            >
              {{ tag.name }}
            </option>
            <option value="CREATE_NEW" class="font-medium text-blue-600">
              + Create New Tag
            </option>
          </select>

          <!-- New Tag Input -->
          <div v-if="showNewTagInput" class="mt-3 animate-fadeIn">
            <input
              v-model="newTagName"
              type="text"
              placeholder="Enter new tag name"
              class="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
              @keyup.esc="cancelNewTag"
            />
            <p class="mt-1 text-xs text-gray-500">Press ESC to cancel</p>
          </div>
        </div>

        <!-- Description -->
        <div v-if="selectedTagId && selectedTagId !== 'CREATE_NEW'" class="animate-fadeIn">
          <label for="description" class="block text-sm font-medium text-gray-700 mb-1.5">
            Notes <span class="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="description"
            v-model="description"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent resize-none"
            placeholder="Add any relevant notes about this tag..."
          />
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-2">
          <button
            type="button"
            @click="closeForm"
            class="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="!canSubmit || submitting"
            class="flex-1 btn-primary"
          >
            <span v-if="submitting" class="flex items-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
            <span v-else>
              {{ selectedTagId === 'CREATE_NEW' ? 'Create & Add' : 'Add Tag' }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'FormInfoTagRelationship',

  props: {
    entityId: {
      type: [Number, String],
      required: true
    },
  },

  data() {
    return {
      selectedTagTypeId: '',
      selectedTagId: '',
      availableTags: [],
      showNewTagInput: false,
      newTagName: '',
      description: '',
      submitting: false,
      tagTypes: []
    };
  },

  computed: {
    ...mapGetters('auth', ['current_user']),

    sortedTagTypes() {
      return [...this.tagTypes].sort((a, b) => a.name.localeCompare(b.name));
    },

    sortedTags() {
      return [...this.availableTags].sort((a, b) => a.name.localeCompare(b.name));
    },

    canSubmit() {
      if (this.selectedTagId === 'CREATE_NEW') {
        return this.newTagName.trim().length > 0;
      }
      return this.selectedTagId && this.selectedTagId !== '';
    }
  },

  methods: {
    async fetchTagTypes() {
      try {
        const response = await axios.get('/api/infotagtypes/');
        this.tagTypes = response.data;
      } catch (error) {
        console.error('Error fetching tag types:', error);
      }
    },

    async fetchTags() {
      if (!this.selectedTagTypeId) {
        this.availableTags = [];
        return;
      }

      try {
        const response = await axios.get('/api/infotags/', {
          params: { infoTagType_id: this.selectedTagTypeId }
        });
        this.availableTags = response.data;
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    },

    onTagTypeChange() {
      this.selectedTagId = '';
      this.showNewTagInput = false;
      this.newTagName = '';
      this.fetchTags();
    },

    onTagChange() {
      if (this.selectedTagId === 'CREATE_NEW') {
        this.showNewTagInput = true;
        this.$nextTick(() => {
          // Focus on the new tag input
          const input = this.$el.querySelector('input[type="text"]');
          if (input) input.focus();
        });
      } else {
        this.showNewTagInput = false;
        this.newTagName = '';
      }
    },

    cancelNewTag() {
      this.selectedTagId = '';
      this.showNewTagInput = false;
      this.newTagName = '';
    },

    async createNewTag() {
      try {
        const response = await axios.post('/api/infotags/', {
          name: this.newTagName.trim(),
          infoTagType: this.selectedTagTypeId,
          UIColor: this.generateColor(),
          content_type_model: 'entity'
        });

        // Add to available tags and select it
        this.availableTags.push(response.data);
        this.selectedTagId = response.data.id;
        this.showNewTagInput = false;
        this.newTagName = '';

        return response.data.id;
      } catch (error) {
        console.error('Failed to create new tag:', error);
        throw error;
      }
    },

    async submitForm() {
      if (!this.canSubmit || this.submitting) return;

      this.submitting = true;

      try {
        let tagId = this.selectedTagId;

        // Create new tag if needed
        if (this.selectedTagId === 'CREATE_NEW') {
          tagId = await this.createNewTag();
        }

        // Create the tag relationship
        const payload = {
          infoTag: tagId,
          entity: this.entityId,
          metaData: this.description.trim() ? { notes: this.description } : {},
          originating_object_id: this.current_user.id,
          originating_object_content_type_model: 'appuser'
        };

        await axios.post('/api/infoTagRelationships/', payload);

        this.$emit('close');
        this.$emit('update');
      } catch (error) {
        console.error('Failed to save tag relationship:', error);
        alert('Failed to save tag. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    closeForm() {
      if (!this.submitting) {
        this.$emit('close');
      }
    },

    generateColor() {
      // Predefined pleasant colors for tags
      const colors = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#8B5CF6', // Violet
        '#EC4899', // Pink
        '#14B8A6', // Teal
        '#F97316', // Orange
        '#6366F1', // Indigo
        '#84CC16', // Lime
        '#06B6D4', // Cyan
        '#A855F7', // Purple
        '#F43F5E', // Rose
        '#22C55E', // Emerald
        '#EAB308', // Yellow
        '#D946EF', // Fuchsia
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    }
  },

  mounted() {
    this.fetchTagTypes();
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}
</style>
